import { useEffect, useState } from "react";
import { API, axios } from "@/api";
import {
  Activity, AlertTriangle, CheckCircle2, Loader2, Clock, Users, Store,
  ShieldCheck, ChevronRight, Zap, Database,
} from "lucide-react";
import { GoogleKillSwitch } from "./GoogleKillSwitch";

// v1.0.0 Batch C — North Star Summary Landing
// Permanent default view at the top of /admin. Single-screen rollup of the
// most important signals across all 12 groups. Reads /api/admin/north-star.

const HEALTH_STYLES = {
  healthy:          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2, dot: "bg-emerald-500", label: "All Clear" },
  needs_attention:  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: AlertTriangle, dot: "bg-amber-400", label: "Needs Attention" },
  urgent:           { bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-700", icon: AlertTriangle, dot: "bg-orange-500", label: "Urgent" },
  critical:         { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", icon: AlertTriangle, dot: "bg-red-500", label: "Critical" },
};

const STATUS_HEX = {
  red: "bg-red-500", orange: "bg-orange-500", yellow: "bg-amber-400",
  green: "bg-emerald-500", blue: "bg-sky-500", purple: "bg-purple-500",
  gray: "bg-slate-300", black: "bg-slate-800",
};

export default function NorthStarPanel({ onGroupClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/north-star`);
        if (alive) setData(res.data);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-center" data-testid="north-star-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const health = HEALTH_STYLES[data.overall_health] || HEALTH_STYLES.healthy;
  const HealthIcon = health.icon;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="north-star-panel">
      {/* Header band — health rollup */}
      <div className={`${health.bg} ${health.border} border-b px-5 py-3 flex items-center gap-3`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${health.dot}`}>
          <HealthIcon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">North Star</p>
          <p className={`text-base font-black ${health.text}`} data-testid="north-star-overall-health">
            {health.label}
            <span className="ml-2 text-[11px] font-semibold text-slate-500">
              {data.panels_needing_attention} of {data.total_panels_tracked} panels need attention
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-400">
          <Clock className="w-3 h-3" />
          {data.generated_at?.slice(11, 16)} UTC
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-100" data-testid="north-star-stats">
        <StatCell
          icon={Store} label="Active / Staging" sub="businesses"
          value={`${data.records.businesses_active.toLocaleString()} / ${data.records.businesses_staging.toLocaleString()}`}
          color={data.records.businesses_staging > data.records.businesses_active * 3 ? "orange" : "green"}
          testId="stat-businesses"
        />
        <StatCell
          icon={Users} label="Users" sub={`+${data.records.users_new_7d} in 7d`}
          value={data.records.users_total.toLocaleString()}
          color={data.records.users_new_7d > 0 ? "blue" : "gray"}
          testId="stat-users"
        />
        <StatCell
          icon={Zap} label="Last Sweep" sub={data.sweep.last_timestamp ? `${data.sweep.days_since}d ago` : "never"}
          value={data.sweep.last_timestamp ? "✓" : "—"}
          color={data.sweep.status_color}
          testId="stat-sweep"
        />
        <StatCell
          icon={ShieldCheck} label="Pending Approvals" sub={`${data.pending_approvals.paid_sweep_queue} paid, ${data.pending_approvals.admin_alerts} alerts`}
          value={data.pending_approvals.total.toLocaleString()}
          color={data.pending_approvals.total > 10 ? "orange" : data.pending_approvals.total > 0 ? "yellow" : "green"}
          testId="stat-pending"
        />
        <StatCell
          icon={Zap} label="Google Places" sub={`of ${data.google_places.monthly_free_calls.toLocaleString()} free calls/mo · ${data.google_places.calls_remaining.toLocaleString()} left`}
          value={`${data.google_places.calls_used.toLocaleString()} used`}
          color={data.google_places.status_color}
          testId="stat-google-places"
        />
      </div>

      {/* iter351 — one-click Google Places kill-switch */}
      <GoogleKillSwitch />

      {/* Budget sub-line */}
      <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center gap-3 text-[11px]" data-testid="north-star-budget-line">
        <Database className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-slate-500">Paid Sweep Budget:</span>
        <span className={`font-bold ${data.paid_sweep_budget.is_active ? "text-emerald-600" : "text-slate-400"}`}>
          ${data.paid_sweep_budget.cap_usd.toFixed(2)} {data.paid_sweep_budget.is_active ? "ACTIVE" : "INACTIVE"}
        </span>
        {!data.paid_sweep_budget.is_active && data.pending_approvals.paid_sweep_queue > 0 && (
          <span className="ml-auto text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
            {data.pending_approvals.paid_sweep_queue} cities awaiting activation
          </span>
        )}
      </div>

      {/* Per-group attention grid */}
      <div className="p-3 space-y-1" data-testid="north-star-groups">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 pt-1 pb-1.5">
          Per-group attention
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {(data.group_attention || []).map((g) => {
            const needsAttn = g.panels_needing_attention > 0;
            return (
              <button
                key={g.group}
                onClick={() => onGroupClick && onGroupClick(g.group)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition ${
                  needsAttn ? "bg-amber-50/60 hover:bg-amber-100" : "hover:bg-slate-50"
                }`}
                data-testid={`north-star-group-${g.group.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_HEX[g.worst_status] || STATUS_HEX.gray} ${
                    g.worst_status === "red" ? "animate-pulse" : ""
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-700 truncate">{g.group}</p>
                  <p className="text-[10px] text-slate-400">
                    {needsAttn ? `${g.panels_needing_attention} of ${g.panels_total} need attention` : `${g.panels_total} panels all clear`}
                  </p>
                </div>
                {needsAttn && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCell({ icon: Icon, label, sub, value, color = "gray", testId }) {
  const dotColor = STATUS_HEX[color] || STATUS_HEX.gray;
  return (
    <div className="bg-white px-4 py-3 flex items-start gap-2.5" data-testid={testId}>
      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor} ${color === "red" ? "animate-pulse" : ""}`} />
        </div>
        <p className="text-base font-black text-slate-800 leading-tight mt-0.5">{value}</p>
        <p className="text-[10px] text-slate-400 truncate">{sub}</p>
      </div>
    </div>
  );
}

// Named helper: keeps signature stable for activity-style usage.
export { Activity };
